import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Money from 'components/Money/Money';
import utilsMessages from '../../../utils/messages';
import FreeShippingIcon from './FreeShippingIcon';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  gap: ${({ gap }) => gap || '0px'};
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #4b5563;
`;

const Bold = styled.span`
  font-weight: 600;
  color: #111827;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ProgressContainer = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  background: #eaebea;
  border-radius: 8px;
  overflow: hidden;
`;
const MoqFlag = styled.div`
  display: ${({ noDisplay }) => (noDisplay ? 'none' : 'block')};
  position: absolute;
  left: ${({ left }) => left};
  top: 0;
  width: 1px;
  height: 3px;
  background: #f38a3e;
`;
const ProgressValue = styled.div`
  position: absolute;
  z-index: 1;
  left: 0;
  top: 0;
  max-width: 100%;
  width: ${({ width }) => width};
  height: 3px;
  background: ${({ color }) => color};
  transition: width 0.4s;
`;

interface IDeliveryFeeSection {
  deliveryFee: any;
  subTotal: number;
  cartMoq: number;
}

const CartTotalProgressSection = (props: IDeliveryFeeSection) => {
  const { deliveryFee, subTotal, cartMoq = 0 } = props;
  const deliveryFeeProgressRef = useRef();

  const isLessThanCartMOQ = useMemo(() => (!!subTotal ? subTotal < cartMoq : true), [cartMoq, subTotal]);

  const isCanReachFreeDelivery = useMemo(
    () => !isLessThanCartMOQ && (deliveryFee?.isFreeDelivery || deliveryFee?.remainingToFree > 0),
    [deliveryFee, isLessThanCartMOQ],
  );

  const deliveryFeeStyles = useMemo(() => {
    if (!deliveryFeeProgressRef?.current || !subTotal || !deliveryFee?.fee) {
      return {
        width: 0,
        progressBackground: '#F38A3E',
      };
    }
    const progressWidth = (deliveryFeeProgressRef?.current as any)?.offsetWidth;

    const destinationMoq =
      (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0) || cartMoq > deliveryFee?.deliveryFeeThreshold
        ? cartMoq
        : deliveryFee?.deliveryFeeThreshold;

    if (!destinationMoq) {
      return {
        width: progressWidth,
        progressBackground: '#6FC373',
      };
    }

    const width = (subTotal / destinationMoq) * progressWidth;

    if (isLessThanCartMOQ) {
      return {
        width: Math.min(width, progressWidth),
        progressBackground: '#F38A3E',
      };
    }

    if (deliveryFee?.isFreeDelivery || deliveryFee?.remainingToFree <= 0) {
      return {
        width: progressWidth,
        progressBackground: '#6FC373',
      };
    }

    return {
      width: Math.min(width, progressWidth),
      progressBackground: '#F38A3E',
    };
  }, [subTotal, isLessThanCartMOQ, deliveryFee, cartMoq, isCanReachFreeDelivery]);

  const TitleComponent = useCallback(() => {
    return (
      <Title>
        {isLessThanCartMOQ ? (
          <>
            <FormattedMessage {...utilsMessages.buyMore} />
            {` `}
            <Bold>
              <Money>{cartMoq - subTotal}</Money>
            </Bold>
            {` `}
            <FormattedMessage {...utilsMessages.canOrder} />
          </>
        ) : !deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0 ? (
          <>
            <FormattedMessage {...utilsMessages.youCanPlaceAnOrder} />
          </>
        ) : deliveryFee?.isFreeDelivery ? (
          <>
            <FormattedMessage {...utilsMessages.freeShippingTitle} />
            {` `}
            <Bold>
              <FormattedMessage {...utilsMessages.freeShippingContent} />
            </Bold>
          </>
        ) : (
          <>
            <FormattedMessage {...utilsMessages.buyMore} />
            {` `}
            <Bold>
              <Money>{deliveryFee?.remainingToFree}</Money>
            </Bold>
            {` `}
            <FormattedMessage {...utilsMessages.toGetFreeService} />
          </>
        )}
      </Title>
    );
  }, [subTotal, deliveryFee, isLessThanCartMOQ, cartMoq]);

  return (
    <Wrapper gap={isCanReachFreeDelivery ? '0px' : '8px'}>
      {TitleComponent()}
      <ProgressWrapper>
        <ProgressContainer ref={deliveryFeeProgressRef}>
          <MoqFlag
            left={`${(cartMoq / deliveryFee?.deliveryFeeThreshold) * (deliveryFeeProgressRef?.current as any)?.offsetWidth}px`}
            noDisplay={
              !cartMoq ||
              cartMoq >= deliveryFee?.deliveryFeeThreshold ||
              (!deliveryFee?.isFreeDelivery && deliveryFee?.remainingToFree <= 0)
            }
          />
          <ProgressValue width={`${deliveryFeeStyles.width}px`} color={deliveryFeeStyles.progressBackground} />
        </ProgressContainer>
        {isCanReachFreeDelivery && <FreeShippingIcon />}
      </ProgressWrapper>
    </Wrapper>
  );
};

export default CartTotalProgressSection;
